import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { seAvisanLosLeads } from '../lib/pantallasDelPanel';

// LA CAMPANA DEL EQUIPO: sus contadores y su lista, en un solo sitio (I6, 12-10).
//
// Vivía dentro de la portada del panel (`AdminDashboard`), así que la campana solo existía
// allí, y con la ventana estrecha no se encontraba. Ahora la crea `AdminLayout` UNA vez, la
// pinta en la cabecera estrecha y se la pasa a las pantallas por el contexto del Outlet: la
// de la cabecera y la de la portada (y la tarjeta de "piden comprar") leen lo mismo.
//
// Novedades reales: leads nuevos sin gestionar, mensajes sin leer y los avisos que el backend
// le deja al equipo. Cada uno ve LO SUYO: el destinatario lo filtra el backend.
//
// Y ES EL ÚNICO QUE PREGUNTA POR LEADS Y MENSAJES (revisión del 12-10): el layout tenía su
// propio sondeo de las dos mismas rutas para las insignias del menú, así que cada minuto
// salían dos veces y el mismo número vivía en dos estados. Las insignias y el aviso de
// "Lead nuevo recibido" leen de aquí. `leadsLeidos` dice si `leads` ya viene del servidor:
// hasta entonces el 0 es "aún no se sabe", no "no hay ninguno".
export function useCampanaDelEquipo() {
    const { api } = useAuth();
    const navigate = useNavigate();
    const [notif, setNotif] = useState({ leads: 0, messages: 0, equipo: 0, ventas: 0, leadsLeidos: false });
    const [avisosEquipo, setAvisosEquipo] = useState({ items: [], cargando: true, error: false });

    // Los contadores, cada uno por su lado: si falla el de leads, los avisos del equipo se
    // siguen contando. En un solo try, una llamada mala dejaba la campana a cero, que se lee
    // como "no hay nada".
    const repasar = useCallback(async () => {
        const pedir = (url) => api.get(url).then(r => r.data).catch(e => {
            console.error('Campana:', url, e);
            return null;
        });
        const [leads, msgs, equipo] = await Promise.all([
            pedir('/leads/stats/summary'),
            pedir('/messages/unread-count'),
            pedir('/notifications/equipo'),
        ]);
        if (equipo) {
            setAvisosEquipo({
                items: Array.isArray(equipo.notifications) ? equipo.notifications : [],
                cargando: false, error: false,
            });
        } else {
            setAvisosEquipo(prev => ({ ...prev, cargando: false, error: true }));
        }
        setNotif(prev => ({
            leads: leads ? (leads.nuevo || 0) : prev.leads,
            messages: msgs ? (msgs.count || 0) : prev.messages,
            // El número sale de la MISMA lista que se pinta: con dos fuentes, el globo dice
            // tres y la lista enseña dos, y entonces no te crees ninguno de los dos.
            equipo: equipo ? (equipo.unread || 0) : prev.equipo,
            ventas: equipo ? (equipo.dinero_sin_leer || 0) : prev.ventas,
            leadsLeidos: prev.leadsLeidos || Boolean(leads),
        }));
    }, [api]);

    useEffect(() => {
        repasar();
        const id = setInterval(repasar, 60000);
        return () => clearInterval(id);
    }, [repasar]);

    /** Marca un aviso del equipo como leído (al abrirlo o al despacharlo desde la tarjeta). */
    const marcarAviso = useCallback(async (aviso) => {
        if (!aviso || aviso.read) return;
        setAvisosEquipo(prev => ({
            ...prev,
            items: prev.items.map(a => (a.id === aviso.id ? { ...a, read: true } : a)),
        }));
        setNotif(prev => ({
            ...prev,
            equipo: Math.max(0, prev.equipo - 1),
            ventas: aviso.dinero ? Math.max(0, prev.ventas - 1) : prev.ventas,
        }));
        try {
            await api.put(`/notifications/equipo/${aviso.id}/read`);
        } catch (e) {
            // Al usuario no se le cuenta: el aviso sigue en la base y vuelve en el próximo
            // repaso. El detalle, a la consola.
            console.error('No se pudo marcar el aviso del equipo:', e);
        }
    }, [api]);

    /** Abrir un aviso: se da por leído y se va a la ficha del cliente, si la tiene. */
    const abrirAviso = useCallback((aviso) => {
        marcarAviso(aviso);
        if (aviso?.link) navigate(aviso.link);
    }, [marcarAviso, navigate]);

    const marcarTodos = useCallback(async () => {
        try {
            await api.put('/notifications/equipo/read-all');
            setAvisosEquipo(prev => ({ ...prev, items: prev.items.map(a => ({ ...a, read: true })) }));
            setNotif(prev => ({ ...prev, equipo: 0, ventas: 0 }));
        } catch (e) {
            console.error('No se pudieron marcar los avisos del equipo:', e);
            toast.error('No se han podido marcar como leídos');
        }
    }, [api]);

    // Con Leads apagado en el menú (26-10) sus leads no suman en la campana.
    const leads = seAvisanLosLeads() ? notif.leads : 0;
    return { notif, total: leads + notif.messages + notif.equipo, avisosEquipo,
        marcarAviso, abrirAviso, marcarTodos };
}
